import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { ConfigProvider } from 'antd-v5';
import { DELIVERY_METHODS, FORM_FIELDS } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.constants';
import { ClientSettingsFormType, GarageDetailsFormProps } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.types';
import { mapClientToFormValues } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.utils';
import { antdTokenForm, DelimSection } from 'pages/MyNetworkPage/GarageDetails/GarageDetailsPage.styled';
import { Box, Flex, Form, InputPrice, Radio, RadioGroup, Switch, Text } from 'UI';
import { textFormatter } from 'utils';
import { getCountryCurrency } from 'utils/countryUtils';

export const GarageDetailsForm: React.FC<GarageDetailsFormProps> = ({
  form,
  onFinish,
  onValuesChange,
  garageDetails,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const manageCreditLimit = AntdForm.useWatch(FORM_FIELDS.MANAGE_CREDIT_LIMIT, form);
  const currency = getCountryCurrency(garageDetails?.countryCode);

  const validationRulesConfig = [
    { required: true, message: t('common.validation.field_required', 'Field is mandatory!') },
  ];
  const statusDisabled = disabled ? '' : undefined;
  const initialValues: ClientSettingsFormType = mapClientToFormValues(garageDetails);

  return (
    <ConfigProvider theme={antdTokenForm}>
      <Form form={form} onFinish={onFinish} initialValues={initialValues} onValuesChange={onValuesChange}>
        <Flex direction={'column'} gap={15}>
          <Text type="heading4">{t('my_network.garage_details.buyers_settings', "Buyer's Settings")}</Text>
          <Flex direction={'column'} gap={10} padding={10}>
            <DelimSection direction={'column'} gap={15}>
              <Flex direction={'column'}>
                <Text type={'h4_paragraph'}>
                  {t('my_network.garage_details.delivery_method', 'Delivery method update')}
                </Text>
                <Text type="base" color="grey35">
                  {t(
                    'my_network.garage_details.delivery_method.description',
                    'Choose whether orders are delivered directly to the garage or available for pick-up at the counter.',
                  )}
                </Text>
              </Flex>
              <Flex direction={'column'} gap={5}>
                <Form.Item
                  name={FORM_FIELDS.DELIVERY_METHOD}
                  rules={validationRulesConfig}
                  validateStatus={statusDisabled}
                  help={statusDisabled}
                >
                  <RadioGroup disabled={disabled}>
                    <Radio
                      value={DELIVERY_METHODS.DIRECT}
                      label={t('my_network.garage_details.delivery_direct', 'Direct')}
                    />
                    <Radio
                      value={DELIVERY_METHODS.DESK}
                      label={t('my_network.garage_details.delivery_desk', 'At the counter')}
                    />
                  </RadioGroup>
                </Form.Item>
              </Flex>
            </DelimSection>
            <DelimSection direction={'row'} justify={'space-between'} align={'center'} minWidth={100} gap={15} inline>
              <Flex direction={'column'}>
                <Text type={'h4_paragraph'}>
                  {t('my_network.garage_details.automatic_order_transfer', 'Orders Automatic validations')}
                </Text>
                <Text type="base" color="grey35">
                  {t(
                    'my_network.garage_details.automatic_order_transfer.description',
                    'Users orders will automatically be validated',
                  )}
                </Text>
              </Flex>
              <Form.Item name={FORM_FIELDS.AUTOMATIC_ORDER_TRANSFER} valuePropName="checked">
                <Switch className={'switch-form'} disabled={disabled} />
              </Form.Item>
            </DelimSection>
            <DelimSection direction={'row'} justify={'space-between'} align={'center'} minWidth={100} gap={15} inline>
              <Flex direction={'column'}>
                <Text type={'h4_paragraph'}>{t('my_network.garage_details.credit_limit', 'Credit limit')}</Text>
                <Text type="base" color="grey35">
                  {t(
                    'my_network.garage_details.credit_limit.description',
                    'Set the maximum outstanding credit amount allowed for this garage',
                  )}
                </Text>
              </Flex>
              <Form.Item name={FORM_FIELDS.MANAGE_CREDIT_LIMIT} valuePropName="checked">
                <Switch className={'switch-form'} disabled={disabled} />
              </Form.Item>
            </DelimSection>
            {manageCreditLimit && (
              <>
                <DelimSection direction={'column'} gap={10}>
                  <Flex direction={'column'}>
                    <Text type={'h4_paragraph'}>
                      {t('my_network.garage_details.max_order_amount', 'Maximum order value')}
                    </Text>
                    <Text type="base" color="grey35">
                      {t('my_network.garage_details.max_order_amount.description', 'Maximum order amount')}
                    </Text>
                  </Flex>
                  <Box width={300}>
                    <Form.Item
                      name={FORM_FIELDS.MAX_ORDER_AMOUNT}
                      rules={validationRulesConfig}
                      validateStatus={statusDisabled}
                      help={statusDisabled}
                      trigger="onBlur"
                      getValueFromEvent={(val?: string) => val || undefined}
                    >
                      <InputPrice currency={currency} bordered disabled={disabled} />
                    </Form.Item>
                  </Box>
                </DelimSection>
                <DelimSection direction={'column'} gap={10} inline>
                  <Flex direction={'column'}>
                    <Text type={'h4_paragraph_grey_blue'}>
                      {t('my_network.garage_details.actual_dms_billing', 'In the process of DMS billing')}
                    </Text>
                    <Text type="base" color="grey35">
                      {textFormatter.formatCurrency(garageDetails?.actualInvoiceAmount ?? '0', currency)}
                    </Text>
                  </Flex>
                </DelimSection>
                <Flex direction={'column'} gap={10}>
                  <Flex direction={'column'}>
                    <Text type={'h4_paragraph'}>
                      {t('my_network.garage_details.max_invoice_amount', 'Maximum billing amount')}
                    </Text>
                    <Text type="base" color="grey35">
                      {t(
                        'my_network.garage_details.max_invoice_amount.description',
                        'Total amount invoiced should not exceed',
                      )}
                    </Text>
                  </Flex>
                  <Box width={300}>
                    <Form.Item
                      name={FORM_FIELDS.MAX_INVOICE_AMOUNT}
                      rules={validationRulesConfig}
                      validateStatus={statusDisabled}
                      help={statusDisabled}
                      trigger="onBlur"
                      getValueFromEvent={(val?: string) => val || undefined}
                    >
                      <InputPrice currency={currency} bordered disabled={disabled} />
                    </Form.Item>
                  </Box>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  );
};
